import { ratingOptions, yesNoOptions, yesNoUnknownOptions } from '../../constants/options';
import { z } from 'zod';
import { getOptionsValues } from '../helpers/schema.helpers';

/**
 * Schema for rating validation.
 * Validates that the input is one of the predefined rating options
 * and transforms the string value to a number.
 */
export const ratingSchema = z.number({
    required_error: "Required",
    invalid_type_error: "Invalid rating type"
}).refine((val) => ratingOptions.includes(val as any), {
    message: "Rating must be between 1 and 5"
});
/**
 * Schema for yes/no/unknown validation.
 * Validates against three possible options and transforms to boolean:
 * - "yes" -> true
 * - "no" or "unknown" -> false
 */
export const yesNoUnknownOptionsSchema = z.enum(
    getOptionsValues(yesNoUnknownOptions),
    { message: "Invalid option" }
).transform(value => (value === "unknown" || value === "no") ? false : true);

/**
 * Schema for basic yes/no validation without transformation.
 * Validates that the input is either "yes" or "no".
 */
export const yesNoOptionsSchema = z.enum(getOptionsValues(yesNoOptions))

/**
 * Schema for yes/no validation with boolean transformation.
 * Extends the basic yes/no schema and transforms:
 * - "yes" -> true
 * - "no" -> false
 */
export const yesNoOptionsTransformedToBoolean = yesNoOptionsSchema
    .transform(value => value === "yes" ? true : false)

export const transformToBoolean = (value: string | number | boolean | string[] | undefined) => {
    if(value !== "yes" && value !== "no") {
        return undefined
    }
    return value === "yes" ? true : false

}

export const transformYesNoToBoolean = (value: typeof yesNoOptions[number]["value"] | null | undefined) => {
    if(!value) {
        return undefined
    }
    return value === "yes" ? true : false
}

export const transformToInt = (value: string | number | boolean | string[] | undefined) => {
    const parsedValue = parseInt(value as string)
    const returnedValue = isNaN(parsedValue) ? undefined : parsedValue
    return returnedValue
}

export const booleanToYesNoSchema: z.ZodEffects<z.ZodOptional<z.ZodBoolean>, typeof yesNoOptions[number]["value"], boolean | undefined> = z
    .boolean()
    .optional()
    .transform(value => value ? "yes" : "no")


export const transformBooleanToYesNo = (value: boolean) => {
    return value ? "yes" : "no"
}
